import React, { useState } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Paper,
  Avatar,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  Tabs,
  Tab
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { useAuth } from '../../context/AuthContext';

// Tab Panel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const Profile = () => {
  const { user, updateProfile, changePassword, error, clearError } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [profileData, setProfileData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [formError, setFormError] = useState('');

  const { firstName, lastName, email } = profileData;
  const { currentPassword, newPassword, confirmPassword } = passwordData;

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // Clear messages when switching tabs
    setSuccess('');
    setFormError('');
    if (error) clearError();
  };

  const onProfileChange = (e) => {
    setProfileData({ ...profileData, [e.target.name]: e.target.value });
    // Clear messages when user starts typing
    setSuccess('');
    setFormError('');
    if (error) clearError();
  };

  const onPasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
    // Clear messages when user starts typing
    setSuccess('');
    setFormError('');
    if (error) clearError();
  };

  const onProfileSubmit = async (e) => {
    e.preventDefault();
    
    // Form validation
    if (!firstName || !lastName || !email) {
      setFormError('Please fill in all fields');
      return;
    }
    
    setLoading(true);
    
    try {
      await updateProfile({
        firstName,
        lastName,
        email
      });
      setSuccess('Profile updated successfully');
      setLoading(false);
    } catch (err) {
      console.error('Profile update error:', err);
      setLoading(false);
    }
  };

  const onPasswordSubmit = async (e) => {
    e.preventDefault();
    
    // Form validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setFormError('Please fill in all password fields');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setFormError('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      setFormError('New password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      await changePassword(currentPassword, newPassword);
      setSuccess('Password changed successfully');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setLoading(false);
    } catch (err) {
      console.error('Password change error:', err);
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="md">
      <Typography variant="h4" component="h1" gutterBottom className="page-header">
        Your Profile
      </Typography>
      
      <Paper elevation={3} sx={{ p: 0 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="profile tabs">
            <Tab label="Profile Information" id="profile-tab-0" />
            <Tab label="Change Password" id="profile-tab-1" />
          </Tabs>
        </Box>
        
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Avatar sx={{ width: 100, height: 100, mb: 2 }} className="profile-avatar">
              <AccountCircleIcon sx={{ width: 80, height: 80 }} />
            </Avatar>
            
            <Typography variant="h6" gutterBottom>
              {user?.firstName} {user?.lastName}
            </Typography>
            
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {user?.role.charAt(0).toUpperCase() + user?.role.slice(1)}
            </Typography>
            
            <Divider sx={{ width: '100%', my: 3 }} />
            
            {(error || formError) && (
              <Alert severity="error" sx={{ mb: 2, width: '100%' }}>
                {formError || error}
              </Alert>
            )}
            
            {success && (
              <Alert severity="success" sx={{ mb: 2, width: '100%' }}>
                {success}
              </Alert>
            )}
            
            <Box component="form" onSubmit={onProfileSubmit} sx={{ mt: 1, width: '100%' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="firstName"
                    required
                    fullWidth
                    id="firstName"
                    label="First Name"
                    value={firstName}
                    onChange={onProfileChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    id="lastName"
                    label="Last Name"
                    name="lastName"
                    value={lastName}
                    onChange={onProfileChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    id="email"
                    label="Email Address"
                    name="email"
                    value={email}
                    onChange={onProfileChange}
                  />
                </Grid>
              </Grid>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3 }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Update Profile'}
              </Button>
            </Box>
          </Box>
        </TabPanel>
        
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography variant="h6" gutterBottom>
              Change Your Password
            </Typography>
            
            {(error || formError) && (
              <Alert severity="error" sx={{ mb: 2, width: '100%' }}>
                {formError || error}
              </Alert>
            )}
            
            {success && (
              <Alert severity="success" sx={{ mb: 2, width: '100%' }}>
                {success}
              </Alert>
            )}
            
            <Box component="form" onSubmit={onPasswordSubmit} sx={{ mt: 1, width: '100%' }}>
              <TextField
                margin="normal"
                required
                fullWidth
                name="currentPassword"
                label="Current Password"
                type="password"
                id="currentPassword"
                value={currentPassword}
                onChange={onPasswordChange}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="newPassword"
                label="New Password"
                type="password"
                id="newPassword"
                value={newPassword}
                onChange={onPasswordChange}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="confirmPassword"
                label="Confirm New Password"
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={onPasswordChange}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3 }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Change Password'}
              </Button>
            </Box>
          </Box>
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default Profile;
